/**
 * <kanze-header>
 * Publishes the header's height as --header-height (other parts position themselves
 * under it) and adds `is-scrolled` once the page has moved, which turns a
 * transparent header solid.
 *
 * On collection pages (class `header--autohide`) the header also sticks to the top,
 * slides away when the shopper scrolls down and comes back when he scrolls up, so the
 * products get the screen. It tells the page through two classes on <html>:
 * `has-sticky-header` (CSS then knows the height it rests at, --header-sticky-height) and
 * `header-hidden`. Whatever docks under the header, such as the category row, moves on that
 * same class, with the same move, so the two travel as one piece.
 *
 * The header only leaves once that row is docked under it, so "hidden" always means "both".
 * Nothing is measured while scrolling: only the scroll position is compared. The height is
 * measured when the header itself changes size (not when the phone's toolbar comes and goes),
 * and `header:resize` is sent when it has changed.
 *
 * On the other pages the header scrolls away with the page (class `header--peek`). It can
 * still come back for a moment, when something has to point at it: a product flying to the bag
 * icon. For that it rests just above the top of the screen once the page has moved past it
 * (CSS, `has-peek-header`), and `show()` slides it down with a transform (`header-peek`).
 * Nothing on the page is laid out again for it.
 */
class KanzeHeader extends HTMLElement {
  connectedCallback() {
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this);
    this.autohide = this.classList.contains('header--autohide');
    this.lastY = window.scrollY;
    this.hidden_ = false;
    if (this.autohide || this.classList.contains('header--sticky')) document.documentElement.classList.add('has-sticky-header');
    this.peeks = this.classList.contains('header--peek');
    if (this.peeks) document.documentElement.classList.add('has-peek-header');
    this.onScroll = () => {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 8);
      if (this.autohide) this.follow(y);
      // Back at the top of the page the header is in its own place again: the peek is over.
      if (this.peeking && y <= this.ownPlace) this.release();
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    // A keyboard user who tabs into a hidden header gets it back.
    this.addEventListener('focusin', () => this.setHidden(false));
    this.onScroll();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
  }

  measure() {
    // The whole part that sticks. Rounded down: what docks under it then never leaves a
    // hairline of the page showing between the two.
    const height = Math.floor((this.closest('.shopify-section') || this).getBoundingClientRect().height);
    if (height === this.height) return;
    this.height = height;
    document.documentElement.style.setProperty('--header-height', `${height}px`);
    document.dispatchEvent(new CustomEvent('header:resize', { detail: { height } }));
  }

  /** Down hides, up shows. Small movements (a few pixels of finger wobble) change nothing. */
  follow(y) {
    const moved = y - this.lastY;
    if (Math.abs(moved) < 6) return;
    this.lastY = y;
    if (document.querySelector('dialog[open]') || this.held) return;
    // Not before the category row has docked: the two leave together or not at all.
    this.setHidden(moved > 0 && y > 160 && !document.querySelector('[data-category-bar]:not(.is-docked)'));
  }

  /** The part of the page that sticks: the header's section. */
  get box() {
    return this.closest('.shopify-section') || this;
  }

  /**
   * Brings the header on screen when it is hidden or has scrolled away, and keeps it there.
   * @param {number} [time] - Let go by itself after this many milliseconds. 0: until release().
   * @returns {boolean} True when the header is on its way to the top of the screen.
   */
  show(time = 0) {
    window.clearTimeout(this.showTimer);
    const box = this.box.getBoundingClientRect();
    let coming = false;
    if (this.autohide) {
      coming = this.hidden_;
      this.held = true;
      this.setHidden(false);
    } else if (this.peeks && (this.peeking || box.bottom <= 1)) {
      coming = true;
      this.peeking = true;
      // Where the header sits in the page itself: under whatever comes before it.
      const before = this.box.previousElementSibling;
      this.ownPlace = before ? before.getBoundingClientRect().bottom + window.scrollY : 0;
      document.documentElement.classList.add('header-peek');
    }
    if (time) this.showTimer = window.setTimeout(() => this.release(), time);
    return coming;
  }

  /** Lets the header go back to what it was doing. */
  release() {
    window.clearTimeout(this.showTimer);
    this.held = false;
    this.peeking = false;
    document.documentElement.classList.remove('header-peek');
  }

  setHidden(hidden) {
    if (hidden === this.hidden_) return;
    this.hidden_ = hidden;
    document.documentElement.classList.toggle('header-hidden', hidden);
    document.dispatchEvent(new CustomEvent('header:toggle', { detail: { hidden } }));
  }
}

/** Reading direction: 1 left to right, -1 right to left. Arrow keys follow what he sees. */
const reading = (element) => (getComputedStyle(element).direction === 'rtl' ? -1 : 1);

/**
 * <header-nav>   the header's links on computers (sections/header.liquid)
 *
 * An audience ("Men") with something to offer has a panel as wide as the page under the
 * header: its categories, its edits and one feature card.
 *
 * - The pointer: the panel opens once the pointer has rested on the link for 120ms and closes
 *   200ms after it has left the link and the panel, so crossing the header on the way to
 *   something else opens nothing. A click on the link goes to the audience's own page.
 * - A finger (a tablet): the first tap opens the panel, the second follows the link. A tap
 *   anywhere else closes it.
 * - The keyboard: the panel opens when its link is focused. Arrow down or Space goes into it;
 *   the arrows move through it (up and down along a column, sideways from column to column);
 *   Escape closes it and returns to the link. Enter on the link follows it, as a click does.
 *
 * The panel itself only slides (CSS); nothing is measured or laid out here.
 */
class HeaderNav extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-nav-item]')].filter((item) => item.querySelector('[data-nav-panel]'));
    this.links = [...this.querySelectorAll('.header__link')];
    this.classList.add('is-ready');
    for (const item of this.items) {
      const link = item.querySelector('[data-nav-link]');
      item.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.plan(item, 120));
      item.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && this.plan(null, 200));
      link.addEventListener('pointerdown', (event) => {
        this.finger = event.pointerType !== 'mouse';
      });
      link.addEventListener('click', (event) => {
        // A finger cannot hover: its first tap opens the panel. A button has nowhere else to go.
        const opens = (this.finger || link.tagName === 'BUTTON') && this.opened !== item;
        this.finger = false;
        if (!opens) return;
        event.preventDefault();
        this.open(item);
      });
      link.addEventListener('focus', () => {
        if (!this.quiet && link.matches(':focus-visible')) this.open(item);
      });
      link.addEventListener('blur', () => {
        this.quiet = false;
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget) && this.opened === item) this.close();
      });
    }
    this.addEventListener('keydown', (event) => this.onKey(event));
    // The cards' photos wait until a panel shows. On a computer, where the panels are, they
    // are fetched once the page is idle, so a panel opens with its photo already there. On a
    // phone these links are not shown and nothing is fetched.
    if (this.offsetParent) {
      const warm = () => this.querySelectorAll('[data-nav-panel] img[loading="lazy"]').forEach((image) => image.setAttribute('loading', 'eager'));
      if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 3000 });
      else window.setTimeout(warm, 1500);
    }
    // Another header link under the pointer: whatever is open gets ready to close.
    for (const link of this.links) {
      if (!this.items.some((item) => item.contains(link))) link.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.plan(null, 200));
    }
    this.onOutside = (event) => {
      if (this.opened && !this.opened.contains(event.target)) this.close();
    };
    this.onHide = (event) => event.detail?.hidden && this.close();
    document.addEventListener('pointerdown', this.onOutside);
    document.addEventListener('header:toggle', this.onHide);
    // In the theme editor, choosing an audience's block shows its panel.
    this.onSelect = (event) => {
      const item = this.items.find((entry) => entry === event.target);
      if (item && this.offsetParent) this.open(item);
    };
    this.onDeselect = () => this.close();
    document.addEventListener('shopify:block:select', this.onSelect);
    document.addEventListener('shopify:block:deselect', this.onDeselect);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    document.removeEventListener('pointerdown', this.onOutside);
    document.removeEventListener('header:toggle', this.onHide);
    document.removeEventListener('shopify:block:select', this.onSelect);
    document.removeEventListener('shopify:block:deselect', this.onDeselect);
  }

  /** One timer for both intents: the last thing the pointer did decides. */
  plan(item, delay) {
    window.clearTimeout(this.timer);
    if (item === (this.opened || null)) return;
    this.timer = window.setTimeout(() => (item ? this.open(item) : this.close()), delay);
  }

  open(item) {
    window.clearTimeout(this.timer);
    if (this.opened === item) return;
    // From one panel straight to another: they change places without sliding again.
    if (this.opened) {
      this.classList.add('is-swapping');
      this.shut(this.opened);
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.classList.remove('is-swapping')));
    }
    this.opened = item;
    item.classList.add('is-open');
    item.querySelector('[data-nav-link]').setAttribute('aria-expanded', 'true');
  }

  shut(item) {
    item.classList.remove('is-open');
    item.querySelector('[data-nav-link]').setAttribute('aria-expanded', 'false');
  }

  close() {
    window.clearTimeout(this.timer);
    if (!this.opened) return;
    this.shut(this.opened);
    this.opened = null;
  }

  onKey(event) {
    const target = event.target instanceof Element ? event.target : null;
    const item = target?.closest('[data-nav-item]');
    const panel = item?.querySelector('[data-nav-panel]');
    const top = target?.closest('.header__link');
    const side = { ArrowRight: 1, ArrowLeft: -1 }[event.key] * reading(this) || 0;

    if (event.key === 'Escape' && this.opened) {
      const link = this.opened.querySelector('[data-nav-link]');
      event.preventDefault();
      this.close();
      // Focus goes back to the link; that focus must not open the panel again.
      this.quiet = true;
      window.Kanze.focus(link);
      return;
    }

    if (top) {
      if (side) {
        const next = this.links[this.links.indexOf(top) + side];
        if (!next) return;
        event.preventDefault();
        window.Kanze.focus(next);
      } else if (panel && (event.key === 'ArrowDown' || (event.key === ' ' && top.tagName !== 'BUTTON'))) {
        event.preventDefault();
        this.open(item);
        window.Kanze.focus(panel.querySelector('a[href], button'));
      }
      return;
    }

    if (!panel?.contains(target)) return;
    const columns = [...panel.querySelectorAll('.nav > *')].map((column) => (column.matches('a[href]') ? [column] : [...column.querySelectorAll('a[href]')])).filter((column) => column.length);
    const at = columns.findIndex((column) => column.includes(target));
    if (at < 0) return;
    const row = columns[at].indexOf(target);
    let next = null;
    if (event.key === 'ArrowDown') next = columns[at][row + 1];
    else if (event.key === 'ArrowUp') next = columns[at][row - 1] || item.querySelector('[data-nav-link]');
    else if (side) next = columns[at + side]?.[Math.min(row, columns[at + side].length - 1)];
    else if (event.key === 'Home') next = columns[0][0];
    else if (event.key === 'End') next = columns[columns.length - 1].at(-1);
    else return;
    event.preventDefault();
    window.Kanze.focus(next);
  }
}

/**
 * <menu-drawer>   the slide-out menu on phones (sections/header.liquid)
 *
 * A tab for each audience (Men | Women). The one he chose is remembered on this device and
 * is the one the drawer opens on next time; until he has chosen, it is the audience whose
 * page he is on. The arrow keys move between the tabs. Opening, closing, the dimmed page
 * behind, Escape and keeping focus inside are the <dialog>'s own (assets/global.js).
 */
const TAB_KEY = 'kanze:menu-audience';

class MenuDrawer extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.dialog = this.closest('dialog');
    if (this.tabs.length) {
      let remembered = null;
      try {
        remembered = window.localStorage.getItem(TAB_KEY);
      } catch (error) {
        /* Private browsing: nothing is remembered, the page's own choice stands. */
      }
      const tab = this.tabs.find((entry) => entry.dataset.tab === remembered);
      if (tab) this.select(tab, { remember: false });
      this.addEventListener('click', (event) => {
        const chosen = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
        if (chosen) this.select(chosen);
      });
      this.addEventListener('keydown', (event) => {
        const current = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
        if (!current) return;
        const at = this.tabs.indexOf(current);
        const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key] * reading(this) || 0;
        let next = null;
        if (step) next = this.tabs[(at + step + this.tabs.length) % this.tabs.length];
        else if (event.key === 'Home') next = this.tabs[0];
        else if (event.key === 'End') next = this.tabs.at(-1);
        if (!next) return;
        event.preventDefault();
        this.select(next);
        window.Kanze.focus(next);
      });
    }
    // In the theme editor, choosing an audience's block opens the drawer on its tab (phones).
    this.onSelect = (event) => {
      const panel = event.target instanceof Element && event.target.matches('[data-panel]') && this.contains(event.target) ? event.target : null;
      if (!panel || !document.querySelector('.header__menu-toggle')?.offsetParent) return;
      const tab = this.tabs.find((entry) => entry.getAttribute('aria-controls') === panel.id);
      if (tab) this.select(tab, { remember: false });
      window.Kanze.openDialog(this.dialog);
    };
    document.addEventListener('shopify:block:select', this.onSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.onSelect);
  }

  select(tab, { remember = true } = {}) {
    for (const entry of this.tabs) {
      const chosen = entry === tab;
      entry.setAttribute('aria-selected', String(chosen));
      if (chosen) entry.removeAttribute('tabindex');
      else entry.setAttribute('tabindex', '-1');
      const panel = document.getElementById(entry.getAttribute('aria-controls'));
      if (panel) panel.hidden = !chosen;
    }
    // A new tab starts at its top.
    if (this.dialog) this.dialog.scrollTop = 0;
    if (!remember) return;
    try {
      window.localStorage.setItem(TAB_KEY, tab.dataset.tab);
    } catch (error) {
      /* Nothing to do: the choice lasts for this page. */
    }
  }
}

if (!customElements.get('kanze-header')) customElements.define('kanze-header', KanzeHeader);
if (!customElements.get('header-nav')) customElements.define('header-nav', HeaderNav);
if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);
